import type { Data } from './types';

const REGION_API_URL = 'https://converge.ai/api/user/region';
const REGION_OVERRIDE_PARAM = 'cookie_region';
const DEBUG_PARAM_NAMES = ['cc_debug', 'cc_mode', 'mockCountry'] as const;

type RegionPayload = Pick<Data, 'countryCode' | 'mode'>;

export function resolveRegionOverrideFromSearch(search: string): RegionPayload | null {
  const params = new URLSearchParams(search);
  const value = (params.get(REGION_OVERRIDE_PARAM) ?? '').trim().toLowerCase();

  if (value === 'eu' || value === 'eea' || value === 'opt_in') {
    return { countryCode: 'DE', mode: 'opt_in' };
  }

  if (
    value === 'non_eu' ||
    value === 'non-eu' ||
    value === 'noneu' ||
    value === 'notice_only'
  ) {
    return { countryCode: 'US', mode: 'notice_only' };
  }

  return null;
}

function buildRegionApiUrl(): string {
  const url = new URL(REGION_API_URL);

  if (typeof window === 'undefined' || !window.location) {
    return url.toString();
  }

  const pageSearch = new URLSearchParams(window.location.search);
  if (pageSearch.get('cc_debug') !== '1') {
    return url.toString();
  }

  for (const name of DEBUG_PARAM_NAMES) {
    const value = pageSearch.get(name);
    if (value) {
      url.searchParams.set(name, value);
    }
  }

  return url.toString();
}

export class RegionClient {
  getOverride(): RegionPayload | null {
    if (typeof window === 'undefined' || !window.location) {
      return null;
    }

    return resolveRegionOverrideFromSearch(window.location.search);
  }

  async fetch(): Promise<RegionPayload> {
    const override = this.getOverride();
    if (override) {
      return override;
    }

    try {
      const res = await fetch(buildRegionApiUrl(), {
        credentials: 'include',
        headers: { Accept: 'application/json' },
        signal: AbortSignal.timeout(5_000),
      });
      if (!res.ok) throw new Error(String(res.status));
      return (await res.json()) as Pick<Data, 'countryCode' | 'mode'>;
    } catch {
      return { countryCode: '', mode: 'opt_in' };
    }
  }
}
